import * as THREE from "three";
import { DockLayout, BoxBase, LayoutBase, TabBase, TabData, PanelBase } from "rc-dock";
import React from "react";
import { assert } from "./utilities/ErrorHandling";
import ScopeCamera from "./Camera";
import store, { StoreActions } from "../state/store";
import { ISceneViewData } from "../interfaces/Misc";
import { IWorkspaceSaveData } from "../interfaces/Workspace";
import { CameraProjectionModes, ApplicationModuleTypes } from "../globals";
import { SpecialLayoutNames } from "../interfaces/enums";

export interface IObjectPreviewRenderData
{
	objects: THREE.Object3D[],
	camera: THREE.Camera,
	context: CanvasRenderingContext2D
}

const windowsMap = new Map<string, { content?: any, ref?: React.RefObject<any> }>();
let cameraPreviewData: any = {};
const sceneViewsDataMap = new Map<string, ISceneViewData>();
let modelTrackerPreviewRenderData: Record<string, any> = {};
let modelTrackerPoseEditorData: any = {};
const objectPreviewRenderData: Record<string, IObjectPreviewRenderData> = {};

export default class WorkspaceManager
{
	static dockLayoutRef = React.createRef<DockLayout>();

	static getDockLayout()
	{
		return this.dockLayoutRef.current;
	}

	static getCustomLayoutByName(name: string)
	{
		const customLayouts = store.getState().workspaceModule.customLayouts;
		const output = 	customLayouts.factoryPresetLayouts.find(layout => layout.name === name) ?? 
						customLayouts.userPresetLayouts.find(layout => layout.name === name);
		return output;
	}
	
	static loadCustomLayoutByName(name: string)
	{
		const customLayout = WorkspaceManager.getCustomLayoutByName(name);
		if(customLayout)
		{
			store.dispatch(StoreActions.workspace.setLayout(customLayout?.layout));
		}
	}

	static loadLayoutPresetByIdx(idx: number, isFactoryPreset = false) // TODO: get rid of this thing
	{
		const customLayouts = store.getState().workspaceModule.customLayouts;
		const item = (isFactoryPreset ? customLayouts.factoryPresetLayouts : customLayouts.userPresetLayouts).at(idx);
		if (item?.layout)
		{
			store.dispatch(StoreActions.workspace.setLayout(item.layout));
		}
	}

	static findTabByID(id: string, layout?: LayoutBase): TabBase | undefined
	{
		layout ??= store.getState().workspaceModule.layout;
		let output: TabBase | undefined;

		function findTabInBox(box: BoxBase): TabBase | undefined 
		{
			let result: TabBase | undefined;
			if (!box?.children) 
			{
				return undefined;
			}
			for (const child of box.children) 
			{
				if ('children' in child) 
				{
					if (result = findTabInBox(child)) 
					{
						break;
					}
				} 
				else if ('tabs' in child) 
				{
					for (const tab of child.tabs) 
					{
						if (tab.id === id) 
						{
							result = tab;
							break;
						}
					}
				}
			}
			return result;
		}

		for(const subtree of Object.values(layout) as Array<BoxBase>)
		{
			if(output = findTabInBox(subtree))
			{
				break;
			}
		}

		return output;
	}

	static findTabParent(tabId: string, layout?: LayoutBase)
	{
		layout ??= store.getState().workspaceModule.layout;
		let output: PanelBase | undefined;

		function findTabParentInBox(box: BoxBase): PanelBase | undefined
		{
			let result: PanelBase | undefined;
			
			if(!box?.children)
			{
				return undefined;
			}
			for (const child of box.children) 
			{
				if ('children' in child) 
				{
					if (result = findTabParentInBox(child)) 
					{
						break;
					}
				} 
				else if ('tabs' in child) 
				{
					for (const tab of child.tabs) 
					{
						if (tab.id === tabId) 
						{
							result = child;
							break;
						}
					}
				}
			}
			return result;			
		}

		for(const subtree of Object.values(layout) as Array<BoxBase>)
		{
			if(output = findTabParentInBox(subtree))
			{
				break;
			}
		}

		return output;
	}

	/**
	 * A specialized case of the "open window" function to handle scene views, since we allow multiple of them  
	 * basically, we just need to ensure they have unique tab ids, while also including the module identifier  
	 * note that we start from `1` because there should always be a scene view window without a suffix
	 *
	 * @static
	 * @return {*} 
	 * @memberof WorkspaceManager
	 */
	static addSceneView(): void
	{
		const dock = WorkspaceManager.getDockLayout();
		if(!dock) return;

		// add a numbered suffix to create a unique tab id for the scene view
		let suffix = 1;
		let tabId = `${ApplicationModuleTypes.SceneView}${suffix}`;
		while (WorkspaceManager.findTabByID(tabId))
		{
			suffix++;
			tabId = `${ApplicationModuleTypes.SceneView}${suffix}`;
		}

		// create the new scene view window
		dock.dockMove({ id: tabId } as TabData, null, "float");
		store.dispatch(StoreActions.workspace.setActiveTab(tabId));
	}

	/**
	 *
	 *
	 * @static
	 * @param {string} windowId
	 * @returns {{id: string, uiInfo: {}, content: Component}} windowData formatted like
	 * - id: unique window instance id string
	 * - uiInfo: object holding ui window rendering data such as size and position
	 * - content: the React.Component that will be rendered
	 * @memberof WorkspaceManager
	 */
	static getWindowData(windowId: string)
	{
		return windowsMap.get(windowId);
	}

	/**
	 * sets the react ref of the window instance with the given id
	 *
	 * @static
	 * @param {string} windowId unique identifier of the window instance
	 * @param {React.RefObject} ref the React ref object of the window instance
	 * @memberof WorkspaceManager
	 */
	static setWindowComponentRef(windowId: string, ref: React.RefObject<any>)
	{
		windowsMap.set(windowId, { ...windowsMap.get(windowId), ref: ref });
	}

	static openWindow(module: ApplicationModuleTypes)
	{
		const dock = WorkspaceManager.getDockLayout();
		if(!dock) return;

		const existingTab = dock.find(module) as TabData;
		if(existingTab)
		{
			dock.dockMove(existingTab, existingTab.parent || null, "middle");
		}
		else
		{
			dock.dockMove({ id: module } as TabData, null, "float");
		}

		store.dispatch(StoreActions.workspace.setActiveTab(module));
	}

	static closeWindow(module: ApplicationModuleTypes)
	{
		const dock = WorkspaceManager.getDockLayout();
		if(!dock) return;
		
		const tab = dock.find(module) as TabData;
		if(tab)
		{
			dock.dockMove(tab, null, "remove");
		}
	}

	/**
	 * stops rendering (hides) all desktop windows
	 *
	 * @static
	 * @memberof WorkspaceManager
	 */
	static closeAllWindows()
	{
		// this layout only has a single scene view (which can't be closed) so it's good for now but we may want to change this into a more robust solution in the future
		WorkspaceManager.loadCustomLayoutByName(SpecialLayoutNames.Simulation);
	}

	/**
	 * removes all windows from existence
	 *
	 * @static
	 * @memberof WorkspaceManager
	 */
	static clearDesktop()
	{
		windowsMap.clear();
		cameraPreviewData = {};
		sceneViewsDataMap.clear();
		modelTrackerPreviewRenderData = {};
		modelTrackerPoseEditorData = {};

		store.dispatch(StoreActions.workspace.clear());
	}

	/**
	 * returns an object containing data about the camera preview and its associated window
	 *
	 * @static
	 * @returns {{id: string, context: CanvasRenderingContext2D, updateCanvasSize: Function, previewCamera: Camera}}
	 * @memberof WorkspaceManager
	 */
	static getCameraPreviewData()
	{
		return cameraPreviewData;
	}

	/**
	 *
	 *
	 * @static
	 * @param {*} data
	 * @memberof WorkspaceManager
	 */
	static setCameraPreviewData(data)
	{
		cameraPreviewData = { ...data };
	}

	/**
	 * sets the value of a property in the camera preview data object
	 *
	 * @static
	 * @param {string} propertyName
	 * @param {*} propertyValue
	 * @memberof WorkspaceManager
	 */
	static setCameraPreviewDataProperty(propertyName: string, propertyValue: any)
	{
		cameraPreviewData[propertyName] = propertyValue;
	}

	static registerSceneView(windowId: string, sceneViewData: ISceneViewData)
	{
		sceneViewsDataMap.set(windowId, sceneViewData);
	}

	static disposeSceneViewData(windowId: string)
	{
		sceneViewsDataMap.delete(windowId);
	}

	static getAllSceneViewsData()
	{
		return sceneViewsDataMap.values();
	}

	/**
	 *
	 *
	 * @static
	 * @param {string} windowId the id of the desktop window associated with the scene view
	 * @returns a data object associated with the scene view that has the given windowId, or undefined if not found
	 * @memberof WorkspaceManager
	 */
	static getSceneViewData(windowId?: string)
	{
		return sceneViewsDataMap.get(windowId || "");
	}

	/**
	 *
	 *
	 * @static
	 * @param {string} windowId the unique id of the window associated with the scene view, used to identify the scene view as well
	 * @param {string} propertyName
	 * @param {*} propertyValue
	 * @memberof WorkspaceManager
	 */
	static setSceneViewDataProperty(windowId: string, propertyName: string, propertyValue: any)
	{
		assert(sceneViewsDataMap.has(windowId));
		sceneViewsDataMap.get(windowId)![propertyName] = propertyValue;
	}

	/**
	 *
	 *
	 * @static
	 * @param {*} data
	 * @memberof WorkspaceManager
	 */
	static setModelTrackerPreviewRenderData(id: string, data: any)
	{
		modelTrackerPreviewRenderData[id] = { ...data };
	}

	static clearModelTrackerPreviewRenderData(id: string)
	{
		delete modelTrackerPreviewRenderData[id];
	}
	
	static getModelTrackerPreviewRenderData()
	{
		return modelTrackerPreviewRenderData;
	}

	static setModelTrackerPoseEditorData(data)
	{
		modelTrackerPoseEditorData = { ...data };
	}

	static getModelTrackerPoseEditorData()
	{
		return modelTrackerPoseEditorData;
	}

	static setObjectPreviewRenderData(id: any, data: IObjectPreviewRenderData)
	{
		objectPreviewRenderData[id] = { ...data };
	}

	static clearObjectPreviewRenderData(id: string)
	{
		delete objectPreviewRenderData[id];
	}

	static getObjectPreviewRenderData()
	{
		return objectPreviewRenderData;
	}

	static getGroupByTabId(tabId: string)
	{
		switch (tabId)
		{
			case "CameraPreview":
				return 'undockable';
			default:
				return 'allowWindow';
		}
	}

	static save(shouldSaveLastLayout = false)
	{
		const output: IWorkspaceSaveData = {
			desktopWindows: {},
			layout: shouldSaveLastLayout ? { ...store.getState().workspaceModule.layout } : undefined,
			customLayouts: 
			{
				userPresetLayouts: [ ...store.getState().workspaceModule.customLayouts.userPresetLayouts ],
			}
		};

		for (const windowId of Object.keys(store.getState().workspaceModule.desktopWindowUiInfo))
		{
			const win = WorkspaceManager.getWindowData(windowId);
			if(!win) continue;

			const content = win.content;
			const { 
				moveable,
				resizable,
				closeable,
				showDuringSimulation,
				showInDefaultState,
				showInTrackersStep,
				showInTemplateEditing,
				showInNormalSteps,
				isTemporaryWindow,
				...uiInfo
			} = store.getState().workspaceModule.desktopWindowUiInfo[windowId]; // removes properties that should not be saved

			// don't save CameraPreview data
			if(content && content.props.tabheader === ApplicationModuleTypes.CameraPreview) continue;

			// don't save temporary windows
			if(isTemporaryWindow) continue;

			// handle scene views
			if (content && content.props.tabheader === ApplicationModuleTypes.SceneView)
			{
				const sceneViewData = WorkspaceManager.getSceneViewData(windowId);
				if (!sceneViewData) continue;
				output.desktopWindows[windowId] = {
					content: content.props.tabheader,
					isMainSceneView: (store.getState().workspaceModule.mainSceneViewId === windowId),
					cameraProjection: (sceneViewData.activeCamera as THREE.PerspectiveCamera).isPerspectiveCamera ? CameraProjectionModes.Perspective : CameraProjectionModes.Orthographic,
					cameraPosition: sceneViewData.activeCamera.position.toArray(), 
					cameraAnchor: sceneViewData.cameraController.anchor.toArray(),
					uiInfo: { ...uiInfo },
				};
			}
			// all other windows
			else if (content && content.props.tabheader === ApplicationModuleTypes.CameraPreview)
			{
				output.desktopWindows[windowId] = {
					content: content.props.tabheader,
					uiInfo: { ...uiInfo },
				};
			}
		}

		return output;
	}

	static load(workspaceData: IWorkspaceSaveData)
	{
		console.log("Loading layout: ", workspaceData);

		const layout = workspaceData?.layout ?? WorkspaceManager.getCustomLayoutByName(SpecialLayoutNames.DefaultWorkspace)?.layout;
		assert(layout, "Layout not found and no default layout found");
		const customLayouts = workspaceData?.customLayouts?.userPresetLayouts ?? [];

		store.dispatch(StoreActions.workspace.setCustomLayouts(customLayouts));
		store.dispatch(StoreActions.workspace.setLayout(layout!));
	}
}
